import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { AxiosInstance } from 'axios';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import Spinner from 'src/components/Spinner';
import { logError } from 'src/utils/Error';

interface IFileChange {
    path: string;
    changeType: string;
}

interface IPluginChange {
    type: string;
    name: string;
    changeType: string;
}

interface IPendingChanges {
    hasChanges: boolean;
    branch: string;
    baseCommit: string;
    files: IFileChange[];
    plugins: IPluginChange[];
    diff: string;
}

interface IReviewCommitModalProps {
    isOpen: boolean;
    workspace: any;
    api: AxiosInstance;
    onClose: () => void;
    onCommitted?: () => void;
}

/**
 * MEL-651 "Review and commit": preview the changes that would be written to the workspace's git
 * repository (pipelines, data stores, settings, plugin adds), then commit them on demand. The diff is
 * read-only; committing replaces the repository with the platform's version.
 */
const ReviewCommitModal: FunctionComponent<IReviewCommitModalProps> = (props) => {
    const { isOpen, workspace, api, onClose, onCommitted } = props;

    const [loading, setLoading] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [changes, setChanges] = useState<IPendingChanges | null>(null);

    const pendingLink = workspace?._links?.['pending changes'];
    const commitLink = workspace?._links?.['commit'];

    const loadChanges = useCallback(() => {
        if (!pendingLink) {
            setError('Reviewing changes is not available for this workspace.');
            return;
        }
        setLoading(true);
        setError(null);
        setChanges(null);
        api.get(pendingLink.href, { errorHandler: false } as any)
            .then((resp: any) => setChanges(resp.data))
            .catch((e: any) => {
                setError(e?.response?.data?.detail || e?.response?.data?.message
                    || 'Could not load pending changes.');
                logError(e);
            })
            .finally(() => setLoading(false));
    }, [api, pendingLink]);

    useEffect(() => {
        if (isOpen) {
            loadChanges();
        }
    }, [isOpen, loadChanges]);

    const handleCommit = () => {
        if (!commitLink) {
            setError('Committing is not available for this workspace.');
            return;
        }
        setCommitting(true);
        setError(null);
        api.post(commitLink.href, {}, { errorHandler: false } as any)
            .then(() => {
                onCommitted && onCommitted();
                onClose();
            })
            .catch((e: any) => {
                setError(e?.response?.data?.detail || e?.response?.data?.message
                    || 'Could not commit changes.');
                logError(e);
            })
            .finally(() => setCommitting(false));
    };

    const hasChanges = !!changes?.hasChanges;

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Review &amp; commit</DialogTitle>
            <DialogContent dividers>
                {loading &&
                    <DialogContentText component="div">
                        <Spinner active>Loading pending changes…</Spinner>
                    </DialogContentText>}

                {!loading && error &&
                    <Alert severity="error">{error}</Alert>}

                {!loading && !error && changes && !hasChanges &&
                    <DialogContentText>No pending changes to commit.</DialogContentText>}

                {!loading && !error && hasChanges && <>
                    <Alert severity="warning" style={{ marginBottom: 16 }}>
                        Committing replaces the repository with the platform&apos;s version. Any changes made
                        directly in the repository will be overwritten.
                        {changes?.baseCommit &&
                            <> Comparing against <code>{changes.baseCommit.substring(0, 7)}</code>
                                {changes.branch ? <> on <code>{changes.branch}</code></> : null}.</>}
                    </Alert>

                    {changes.plugins && changes.plugins.length > 0 && <>
                        <Typography variant="subtitle2">Plugins</Typography>
                        <List dense>
                            {changes.plugins.map((p) => (
                                <ListItem key={`${p.type}/${p.name}`} disableGutters>
                                    <ListItemText primary={`${p.changeType}: ${p.name}`}
                                        secondary={p.type.toLowerCase()} />
                                </ListItem>
                            ))}
                        </List>
                        <Divider style={{ margin: '8px 0' }} />
                    </>}

                    {changes.files && changes.files.length > 0 && <>
                        <Typography variant="subtitle2">Files</Typography>
                        <List dense>
                            {changes.files.map((f) => (
                                <ListItem key={f.path} disableGutters>
                                    <ListItemText primary={f.path} secondary={f.changeType} />
                                </ListItem>
                            ))}
                        </List>
                    </>}

                    {changes.diff &&
                        <pre style={{
                            maxHeight: 320,
                            overflow: 'auto',
                            background: '#f5f5f5',
                            padding: 12,
                            fontSize: 12,
                            whiteSpace: 'pre',
                        }}>{changes.diff}</pre>}
                </>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={committing}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCommit}
                    disabled={loading || committing || !hasChanges}>
                    <Spinner active={committing}>Commit</Spinner>
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewCommitModal;
